import React, { useState, useRef, useContext } from 'react';
import { SecondaryButton } from 'jl-ui-kit/elements/button/Button';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styles from './MobileMenu.scss';
import MobileMenuItem from '../mobile-menu-item/MobileMenuItem';
import SubMenu from './SubMenu';

import EnvVarsFeaturesContext from '../../../scaffold/EnvVarsFeaturesContext';
import { getStaticMobileMenus } from '../../links';
import { UserContext } from '../../../scaffold/UserContext';
import NestedMenu from './NestedMenu';

const emptyMenu = { name: '', backLabel: '', child: [] };

const MobileMenu = ({ menus, fetchChildCategories }) => {
  const [activeData, setActiveData] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const top = useRef();

  const {
    userState: {
      user: { signedIn },
    },
  } = useContext(UserContext);

  const {
    envVars: { accountBaseUrl },
  } = useContext(EnvVarsFeaturesContext);

  const staticMenus = getStaticMobileMenus(accountBaseUrl, signedIn);

  const scrollToTop = () => {
    if (top.current && top.current.scrollBy) {
      top.current.scrollBy(0, -1000);
    }
  };

  const updateSubMenuPosition = index => {
    scrollToTop();
    setActiveIndex(index);
  };

  const updateSubMenuData = async (menuItem, backLabel, menuIndex) => {
    let currentMenuItem = menuItem;

    if (menuIndex === 1) {
      try {
        currentMenuItem = await fetchChildCategories({
          id: menuItem.id,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(`fetch failed for category id: ${menuItem.id}`);
      }
    }

    const newItem = {
      ...currentMenuItem,
      backLabel,
    };
    const newMenus = [...activeData];
    newMenus[menuIndex] = newItem;

    setActiveData(newMenus);
  };

  return (
    <>
      <nav
        className={styles.mobileMenu}
        ref={top}
        data-testid="mobile-navigation-menu"
      >
        <div
          role="menu"
          className={classNames(styles.menu, {
            [styles.offScreenLeft]: activeIndex !== 0,
          })}
        >
          {menus.concat(staticMenus).map(menu => (
            <React.Fragment key={menu.name}>
              <h3 className={styles.mobileMenu__name}>{menu.name}</h3>
              {menu.text && (
                <p className={styles.mobileMenu__text}>{menu.text}</p>
              )}
              {menu.child && (
                <ul className={styles.mobileMenuList}>
                  {menu.child.map(menuItem => {
                    const { name, url, child } = menuItem;
                    return (
                      <MobileMenuItem
                        key={name}
                        label={name}
                        url={url}
                        onClick={() => {
                          updateSubMenuData(menuItem, menu.name, 0);
                          updateSubMenuPosition(1);
                        }}
                        child={child}
                      />
                    );
                  })}
                </ul>
              )}
              {menu.button && (
                <div className={styles.mobileMenu__button}>
                  <SecondaryButton
                    tag="a"
                    href={menu.button.url}
                    {...{ target: '_blank', rel: 'noopener' }}
                  >
                    {menu.button.label}
                  </SecondaryButton>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {[...activeData, emptyMenu].map((data, i) => {
          const level = i + 1;

          const hierarchy = activeData.slice(0, level - 1).map(d => d.name);

          return level === 3 ? (
            <NestedMenu
              key={level}
              open={activeIndex === level}
              data={data}
              onNavigateBack={() => {
                updateSubMenuPosition(level - 1);
              }}
              hierarchy={hierarchy}
            />
          ) : (
            <SubMenu
              key={level}
              open={activeIndex === level}
              subMenuData={data}
              onNavigateBack={() => {
                updateSubMenuPosition(level - 1);
              }}
              onItemSelected={menuItem => {
                updateSubMenuData(menuItem, data.name, level);
                updateSubMenuPosition(level + 1);
              }}
              offScreenLeft={activeIndex === level + 1}
              hierarchy={hierarchy}
            />
          );
        })}
      </nav>
    </>
  );
};

const itemShape = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  url: PropTypes.string,
};
itemShape.child = PropTypes.arrayOf(PropTypes.shape(itemShape));

MobileMenu.propTypes = {
  menus: PropTypes.arrayOf(PropTypes.shape(itemShape)).isRequired,
};

export default MobileMenu;
